import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { AlertTriangle, Loader2, Euro, ShieldCheck, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { getCancellationPreview, cancelWithCompensation } from '../../services/api';

const eur = (n) => `€${(Number(n) || 0).toFixed(2)}`;

const Row = ({ label, value, hint, strong, testId, tone }) => (
  <div className="flex items-start justify-between gap-3 py-2 border-b border-black/5 last:border-0" data-testid={testId}>
    <div className="min-w-0 flex-1">
      <p className={strong ? 'text-sm font-semibold text-gray-900' : 'text-sm text-gray-600'}>{label}</p>
      {hint && <p className="text-[11px] text-gray-500 mt-0.5">{hint}</p>}
    </div>
    <span className={`shrink-0 tabular-nums whitespace-nowrap ${strong ? 'text-base font-extrabold' : 'text-sm font-medium'} ${
      tone === 'green' ? 'text-[#1B5E20]' : tone === 'blue' ? 'text-blue-700' : 'text-gray-800'
    }`}>{value}</span>
  </div>
);

export const CancelWithCompensationDialog = ({ delivery, open, onOpenChange, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [confirmFinal, setConfirmFinal] = useState(false);
  const [typedAmount, setTypedAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !delivery) return;
    setLoading(true);
    setPreview(null);
    setAmount('');
    setReason('');
    setConfirmFinal(false);
    setTypedAmount('');
    getCancellationPreview(delivery.id)
      .then((res) => setPreview(res.data))
      .catch((err) => toast.error(err?.response?.data?.detail || 'Could not load the cancellation figures'))
      .finally(() => setLoading(false));
  }, [open, delivery]);

  const comp = Math.max(0, Number(amount) || 0);
  const max = Number(preview?.max_compensation_eur) || 0;
  const authorized = Number(preview?.authorized_total_eur) || 0;
  const released = Math.max(0, +(authorized - comp).toFixed(2));
  const overMax = comp > max + 0.001;
  const canReceive = !!preview?.driver?.can_receive_transfer;
  const amountsMatch = Math.abs((Number(typedAmount) || -1) - comp) < 0.005;
  const ready = !!preview?.eligible && !overMax && reason.trim().length >= 10 && confirmFinal && amountsMatch && !submitting;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await cancelWithCompensation(delivery.id, {
        compensation_eur: comp,
        reason: reason.trim(),
        confirm_final: true,
        acknowledge_queued_payout: !canReceive,
      });
      if (data.transfer_status === 'paid' || data.transfer_status === 'not_required') {
        toast.success(`Cancelled — ${eur(data.compensation_eur)} paid to the driver, ${eur(data.released_to_customer_eur)} released to the customer.`);
      } else {
        toast.warning(`Cancelled — ${eur(data.compensation_eur)} captured but the transfer is queued (${data.transfer_status}). It appears under "Compensation owed".`);
      }
      onOpenChange(false);
      onDone?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cancellation failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)] sm:w-full max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="cancel-compensation-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">
            Cancel &amp; compensate driver
            <span className="block text-xs font-normal text-gray-500 mt-1">
              {delivery?.delivery_ref || delivery?.id?.slice(0, 8)}
            </span>
          </DialogTitle>
        </DialogHeader>

        {loading && (          <div className="py-10 text-center text-sm text-gray-500" data-testid="comp-loading">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading payment details…
          </div>
        )}

        {!loading && preview && !preview.eligible && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="comp-ineligible">
            <p className="font-semibold mb-1 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Not available for this delivery</p>
            <p>{preview.ineligible_reason}</p>
          </div>
        )}

        {!loading && preview?.eligible && (
          <div className="space-y-4">
            <div className="rounded-md border border-gray-200 p-3 bg-gray-50/60">
              <Row label="Customer authorised (held)" value={eur(preview.authorized_total_eur)} strong testId="comp-authorized" />
              <Row label="Driver bid" value={eur(preview.driver_bid_eur)} testId="comp-bid" />
              <Row label="Booking Deposit" value={eur(preview.customer_booking_fee_eur)} hint="Never used to fund compensation" testId="comp-booking-fee" />
              <Row label="JustDeliver Driver Fee (10%)" value={eur(preview.driver_access_fee_eur)} hint="Not charged on a cancellation" testId="comp-access-fee" />
              <Row label="Payout had it completed" value={eur(preview.standard_driver_payout_eur)} hint="For context only" testId="comp-standard-payout" />
            </div>

            <div>
              <Label className="text-xs text-gray-600">Driver compensation (max {eur(max)} — the driver's bid)</Label>
              <div className="relative mt-1">
                <Euro className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  type="number" min="0" step="0.01" max={max}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className={`pl-9 tabular-nums ${overMax ? 'border-red-500' : ''}`}
                  data-testid="comp-amount-input"
                />
              </div>
              {overMax && (
                <p className="text-xs text-red-600 mt-1" data-testid="comp-amount-error">
                  Cannot exceed the driver's bid of {eur(max)}.
                </p>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                {[0, 0.25, 0.5, 1].map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setAmount((max * f).toFixed(2))}
                    className="text-xs px-2.5 py-1 rounded-full border border-gray-300 hover:bg-gray-100"
                    data-testid={`comp-quick-${f}`}
                  >
                    {f === 0 ? '€0' : f === 1 ? `Full bid ${eur(max)}` : `${f * 100}% ${eur(max * f)}`}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-md border border-gray-200 p-3">
              <Row label="Driver receives (gross)" value={eur(comp)} strong tone="green" testId="comp-driver-receives" />
              <Row label="Released back to the customer" value={eur(released)} strong tone="blue" hint="Hold is voided by Stripe — no refund wait" testId="comp-release-amount" />
              <Row label="JustDeliver retains" value={eur(0)} hint="Card processing cost on the captured amount is absorbed by JustDeliver" testId="comp-retained" />
            </div>

            <div className={`rounded-md border p-3 text-xs flex gap-2 ${canReceive ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-amber-300 bg-amber-50 text-amber-900'}`} data-testid="comp-driver-status">
              {canReceive ? <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" /> : <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />}
              <div className="min-w-0 break-words">
                <p className="font-semibold">{preview.driver?.name || 'Driver'} — {canReceive ? 'ready to receive transfers' : 'payout setup incomplete'}</p>
                {!canReceive && <p className="mt-0.5 break-all">{preview.driver?.reason}</p>}
                <p className="mt-0.5 text-[11px] opacity-80">
                  Payment: {preview.pi_status} · held {eur(preview.amount_capturable_eur)}
                  {preview.platform_balance_eur !== null && ` · platform balance ${eur(preview.platform_balance_eur)}`}
                </p>
              </div>
            </div>

            <div>
              <Label className="text-xs text-gray-600">Cancellation reason / notes (required, stored in the audit trail)</Label>
              <Textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Customer cancelled while the driver was 20 minutes into a 40 minute journey."
                className="mt-1 text-sm"
                data-testid="comp-reason-input"
              />
              {reason.trim().length > 0 && reason.trim().length < 10 && (
                <p className="text-xs text-red-600 mt-1">Please write at least 10 characters.</p>
              )}
            </div>

            <div className="rounded-md border-2 border-red-300 bg-red-50 p-3 space-y-2.5">
              <p className="text-sm font-bold text-red-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> This is FINAL and cannot be increased later
              </p>
              <p className="text-xs text-red-800 leading-relaxed">
                Capturing {eur(comp)} permanently releases the remaining {eur(released)} of the customer's hold.
                Stripe does not allow a second capture on the same authorisation, so you cannot top the driver up afterwards.
              </p>
              <label className="flex items-start gap-2 text-xs text-red-900">
                <Checkbox checked={confirmFinal} onCheckedChange={(v) => setConfirmFinal(!!v)} data-testid="comp-confirm-checkbox" />
                <span>I understand this is final and irreversible.</span>
              </label>
              <div>
                <Label className="text-[11px] text-red-900">Type the compensation amount to confirm ({comp.toFixed(2)})</Label>
                <Input
                  value={typedAmount}
                  onChange={(e) => setTypedAmount(e.target.value)}
                  placeholder={comp.toFixed(2)}
                  className="mt-1 h-9 text-sm tabular-nums bg-white"
                  data-testid="comp-typed-amount-input"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="comp-cancel-btn">
                Back
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={submit}
                disabled={!ready}
                data-testid="comp-submit-btn"
              >
                {submitting ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing…</>) : `Capture ${eur(comp)} & cancel`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CancelWithCompensationDialog;
